import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VideocamIcon from '@mui/icons-material/Videocam';
import PushPinIcon from '@mui/icons-material/PushPin';
import { useOnClickOutside } from 'usehooks-ts';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import cs from 'classnames';

import { selectShareComposer, switchShareModalComposer } from '../../../store/composers/composersSlice';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import DeleteComposerModal from '../../MyReflexComposers/DeleteComposerModal';
import { postVideofeedViewRequest, updateVideofeedTitle } from '../../../api';
import DownloadDataModal from '../../modules/VideoFileList/DownloadDataModal';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { clearVideoFeeds } from '../../../store/videos/videoSlice';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import { bytesToMB, useDimensions } from '../../../helpers';
import { MoreIcon } from '../../../assets/icons/threedots';
import DownloadButton from '../../modules/DownloadButton';
import Icon from '../../VideofeedsList/Videofeed/icon';
import { bytesToGB } from '../../../helpers/bytesToGB';
import { RolesLowerCase } from '../../../enums/ROLES';
import { Portal } from '../../../helpers/bodyPortal';
import { useOverflowAwareActionMenu } from '../../../helpers/useOverflowAwareActionMenu';
import ShareButton from '../../modules/ShareButton';
import styles from './SharedComposer.module.scss';
import { VIDEOFEED } from '../../../enums/TYPES';
import RolePlate from '../../modules/RolePlate';
import { PATHS } from '../../../enums/PATHS';
import { openDownloadManager } from '../../../store/downloadManager/downloadManagerSlice';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';
import FileTypeThumbnail from '../../modules/FileTypeThumbnail';
import ReplaceThumbnailModal from '../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../api/videofeeds';
import ThumbnailButton from '../../modules/ThumbnailButton';

type TComposer = {
  composer: VIDEOFEED;
  owner?: boolean;
  feedIndex?: number;
  sharedComposer?: boolean;
  noActions?: boolean;
  setModalType: (t: string) => void;
  pinContextType?: string;
  pinContextParentId?: string | number | null;
  onPinUpdated?: () => void;
  dateSortOption?: SortOptions | null;
};

const SharedComposer: React.FC<TComposer> = ({
  composer,
  owner = true,
  feedIndex,
  sharedComposer,
  noActions,
  setModalType,
  pinContextType,
  pinContextParentId,
  onPinUpdated,
  dateSortOption,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(composer.thumbnail_url || '');

  const [title, setTitle] = useState(composer.title);
  const [isEditing, setIsEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const notification = useSnackbar();
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const previewUrl = thumbnailUrl || composer?.preview_url || composer?.videos?.[0]?.s3_preview_url || '';
  const organizationHaveDownloadHours = Number(myOrganization?.download_hours_limit || 0) > 0;
  const canShowMyComposerDownload = composer?.type !== VIDEOFEED_TYPE.MY_COMPOSER || organizationHaveDownloadHours;
  const canDownload = canShowMyComposerDownload && !!composer.highest_user_access?.allow_downloads;
  const isPinned = Boolean(composer?.is_pinned);
  const canReplaceThumbnail =
    composer.created_by?.id === currentUser?.id ||
    composer.highest_user_access?.access?.title === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE;

  useEffect(() => {
    setThumbnailUrl(composer.thumbnail_url || '');
  }, [composer.thumbnail_url]);

  const { created_by } = composer;

  const role = useMemo(() => {
    if (created_by?.role?.title && created_by?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN) {
      return 'OA';
    } else if (created_by?.role?.title) {
      let role = created_by.role.title;
      // @ts-ignore
      if (created_by?.organization_users) {
        // @ts-ignore
        if (created_by?.organization_users?.role?.title) {
          // @ts-ignore
          role = created_by.organization_users.role.title;
        }
      }
      return role;
    } else {
      return '';
    }
  }, [created_by]);

  const switchDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const onShareClick = () => {
    setShowDotsMenu(false);
    setModalType('composer');
    dispatch(selectShareComposer(composer));
    dispatch(switchShareModalComposer(true));
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };
  const onPinToggle = async () => {
    try {
      const { pinContent, unpinContent } = await import('../../../api/pinnedContentApi');
      if (isPinned) {
        await unpinContent({
          targetType: 'videofeed',
          targetId: composer.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      } else {
        await pinContent({
          targetType: 'videofeed',
          targetId: composer.id,
          contextType: pinContextType!,
          contextParentId: pinContextParentId,
        });
      }
      onPinUpdated?.();
      notification.showMessage(isPinned ? 'Unpinned' : 'Pinned');
      setShowDotsMenu(false);
    } catch {
      notification.showMessage('Failed to update pin');
    }
  };
  type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;
  const redirectToVideoPage = (e: CustomEventHandler) => {
    if (!composer.created_by) {
      notification.showMessage('Author of this composer have been deleted');
      return;
    }
    if (noActions) return;
    if (e.target === e.currentTarget) {
      postVideofeedViewRequest(composer.id);
      history.push(
        `${PATHS.COMPOSER_PAGE.path}?feedId=${composer.id}&fileside=true&feed=${feedIndex}&clipId=${
          composer.videos[0].id
        }&clip=0&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${sharedComposer ? '&sharedComposer=true' : ''}`,
      );
    }
    dispatch(clearVideoFeeds());
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const switchDownloadDataModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const onDownload = (e: any) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    dispatch(
      openDownloadManager({
        feedId: String(composer.id),
        feedTitle: composer.title,
      }),
    );
  };

  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ x: number; y: number } | null>(null);
  const { actionMenuRef, triggerRef, menuPosition } = useOverflowAwareActionMenu({
    showMenu: showDotsMenu,
    anchorPosition: menuAnchorPosition,
  });

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      setMenuAnchorPosition(null);
      return;
    }

    if (e.type === 'contextmenu') {
      setMenuAnchorPosition({ x: e.clientX, y: e.clientY });
    } else {
      setMenuAnchorPosition(null);
    }
    setShowDotsMenu(true);
  };

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
    setMenuAnchorPosition(null);
  });

  const onHover = () => {
    setHover(!hover);
  };

  const handleEdit = async () => {
    if (noActions) return;
    if (title.trim().length === 0) {
      setTitle(composer.title);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);
    await updateVideofeedTitle(composer.id, title);
    setHover(false);

    if (composer.title !== title) window.location.reload();
  };

  const { widthD } = useDimensions();
  return (
    <div
      style={{
        position: 'relative',
        boxShadow: isPinned ? 'inset 0 0 0 1px rgba(57, 179, 234, 0.35)' : undefined,
      }}
      className={cs(styles.container, showDotsMenu && styles.selected)}
      onClick={redirectToVideoPage}
      onContextMenu={switchDotsMenu}
    >
      <div className={styles.leftSide} onClick={redirectToVideoPage}>
        <FileTypeThumbnail
          className={styles.fileTypeThumbnail}
          icon={
            <Icon onClick={redirectToVideoPage}>
              <ComposerIcon
                className={styles.composerIcon}
                style={{ pointerEvents: 'none', color: '#39b3ea' }}
                onClick={redirectToVideoPage}
              />
            </Icon>
          }
        >
          {previewUrl ? (
            <img src={previewUrl} alt={'video feed'} className={styles.thumbnail} />
          ) : (
            <VideocamIcon className={styles.thumbnail} />
          )}
        </FileTypeThumbnail>
        <div />
        {/* {isEditing ? (
            <form onSubmit={handleEdit}>
              <input className={styles.input} onChange={handleChange} defaultValue={composer.title} onBlur={handleEdit} />
            </form>
          ) : ( */}
        <div onClick={redirectToVideoPage} className={hover ? styles.hovered : styles.name}>
          {composer.title}
        </div>
        {/* )} */}
      </div>
      <div className={styles.owner}>
        <Tooltip title={composer?.created_by?.username}>
          <span>{composer?.created_by?.username}</span>
        </Tooltip>
      </div>
      <div className={styles.access}>
        {composer?.highest_user_access ? ACCESS_TYPE[composer.highest_user_access.access.title as ACCESS_TYPE_ENUM] : ''}
      </div>
      <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(composer, dateSortOption), true)}`}>
        <div className={styles.date}>
          {widthD > 1300
            ? DateFormatter(getDateColumnValue(composer, dateSortOption), true)
            : DateFormatter(getDateColumnValue(composer, dateSortOption))}
        </div>
      </Tooltip>
      <div className={styles.size}>
        <img src={StorageIcon} alt="storage" />
        {bytesToGB(Number(composer?.size)) > 1
          ? `${bytesToGB(Number(composer?.size), 2)} GB`
          : `${bytesToMB(Number(composer?.size), 0) || 1} MB`}
      </div>

      <div className={styles.dotsMenuBtn}>
        <div ref={triggerRef} className={styles.dotIcon} onClick={switchDotsMenu}>
          <MoreIcon />
        </div>
      </div>

      <Portal>
        <div
          ref={actionMenuRef}
          className={styles.actionMenu}
          style={{
            position: 'fixed',
            display: showDotsMenu ? 'block' : 'none',
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            right: 'auto',
            cursor: 'auto',
          }}
        >
          <div
            className={cs(
              styles.menuOption,
              (noActions &&
                !composer?.created_by &&
                composer?.highest_user_access &&
                composer?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE &&
                composer?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE) ||
                !composer?.highest_user_access
                ? styles.disabled
                : '',
            )}
            onClick={onShareClick}
          >
            <ShareButton
              disabled={
                (noActions &&
                  !composer?.created_by &&
                  composer?.highest_user_access &&
                  composer?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE &&
                  composer?.highest_user_access.access.title !== ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE) ||
                !composer?.highest_user_access
              }
            />{' '}
            Share
          </div>
          {canReplaceThumbnail && (
            <div className={styles.menuOption} onClick={onReplaceThumbnail}>
              <ThumbnailButton /> Replace Thumbnail Image
            </div>
          )}
          {canDownload ? (
            <div className={styles.menuOption} onClick={onDownload}>
              <DownloadButton /> Download
            </div>
          ) : null}
          {pinContextType && (
            <div className={styles.menuOption} onClick={onPinToggle}>
              <span
                style={{
                  display: 'inline-flex',
                  width: 32,
                  height: 30,
                  padding: 4,
                  borderRadius: 'var(--ui-border-radius)',
                  background: 'var(--action-menu-icon-bg)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12,
                  boxSizing: 'border-box',
                }}
              >
                <PushPinIcon sx={{ fontSize: 20, color: '#fff' }} />
              </span>
              {isPinned ? 'Unpin' : 'Pin'}
            </div>
          )}
        </div>
      </Portal>

      <ReplaceThumbnailModal
        open={showThumbnailModal}
        setOpen={setShowThumbnailModal}
        onUpload={(data) => uploadVideofeedThumbnail(composer.id, data)}
        onUploaded={(updatedComposer) => setThumbnailUrl(updatedComposer.thumbnail_url || '')}
        onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(composer.id) : undefined}
        preview={{ type: 'file', title, imageUrl: previewUrl }}
      />

      <DeleteComposerModal switchModal={switchDeleteModal} open={showDeleteModal} composerId={composer.id} />
      <DownloadDataModal switchModal={switchDownloadDataModal} isOpen={showDownloadModal} feedId={composer.id} />
    </div>
  );
};

export default SharedComposer;
